import { Archive, ExternalLink, Inbox, Loader2, Plus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSubmitUrlMutation } from '@/hooks/useLinks';
import { decodeGlobalId } from '@/lib/global-id';
import { cn } from '@/lib/utils';

// Response type for submit URL mutation
interface SubmitUrlResponse {
	queued: boolean;
	message?: string | null;
	entry?: { id: string; url: string; status: string } | null;
	existingLink?: { id: string; url: string; title: string; queueId?: number; archivedAt?: string | null } | null;
}

type DialogState = 'input' | 'checking' | 'success' | 'duplicate' | 'error';

interface QuickAddDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onNavigateToLink: (linkId: number, queueId: number, isArchived: boolean) => void;
}

export function QuickAddDialog({ open, onOpenChange, onNavigateToLink }: QuickAddDialogProps) {
	const [state, setState] = useState<DialogState>('input');
	const [url, setUrl] = useState('');
	const [response, setResponse] = useState<SubmitUrlResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [shiftHeld, setShiftHeld] = useState(false);
	const [addedCount, setAddedCount] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const submitUrl = useSubmitUrlMutation();

	// Track shift key state
	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Shift') setShiftHeld(true);
		}
		function handleKeyUp(e: KeyboardEvent) {
			if (e.key === 'Shift') setShiftHeld(false);
		}
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	// Reset state when dialog opens
	useEffect(() => {
		if (open) {
			setState('input');
			setUrl('');
			setResponse(null);
			setError(null);
			setAddedCount(0);
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	}, [open]);

	const handleSubmit = useCallback(
		async (addMore: boolean) => {
			const trimmedUrl = url.trim();
			if (!trimmedUrl) return;

			// Basic URL validation
			try {
				new URL(trimmedUrl);
			} catch {
				setError('Invalid URL format');
				setState('error');
				return;
			}

			setState('checking');
			setError(null);

			try {
				const result = await submitUrl.mutateAsync(trimmedUrl, 'quick-add');
				if (!result) throw new Error('No response from server');

				setResponse(result as SubmitUrlResponse);

				const isDuplicate =
					!result.queued &&
					(result.existingLink ||
						result.entry?.status === 'pending' ||
						result.entry?.status === 'processing');

				if (addMore) {
					// Keep dialog open, clear input, ready for next URL
					if (!isDuplicate) setAddedCount((c) => c + 1);
					setUrl('');
					setState('input');
					setError(isDuplicate ? 'Already exists' : null);
					setTimeout(() => inputRef.current?.focus(), 0);
				} else {
					// Auto-close on success/duplicate
					if (isDuplicate) {
						setState('duplicate');
					} else {
						onOpenChange(false);
					}
				}
			} catch (err) {
				console.error('Failed to submit link:', err);
				setError(err instanceof Error ? err.message : 'Failed to submit link');
				setState('error');
			}
		},
		[url, onOpenChange, submitUrl]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && state === 'input') {
				e.preventDefault();
				handleSubmit(e.shiftKey);
			}
		},
		[state, handleSubmit]
	);

	const handleGoToLink = useCallback(() => {
		if (response?.existingLink) {
			const linkId = decodeGlobalId(response.existingLink.id);
			onNavigateToLink(
				linkId,
				response.existingLink.queueId ?? 0,
				response.existingLink.archivedAt !== null
			);
			onOpenChange(false);
		}
	}, [response, onNavigateToLink, onOpenChange]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent showCloseButton={false} className="max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Plus className="size-5" />
						Quick Add Link
						{addedCount > 0 && (
							<span className="ml-auto text-xs font-normal text-muted-foreground">
								{addedCount} added
							</span>
						)}
					</DialogTitle>
				</DialogHeader>

				{/* Input State */}
				{(state === 'input' || state === 'error') && (
					<div className="space-y-4">
						<div className="space-y-2">
							<input
								ref={inputRef}
								type="text"
								placeholder="Paste URL here..."
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								onKeyDown={handleKeyDown}
								className={cn(
									'w-full h-10 px-3 text-sm bg-transparent border rounded-md outline-none',
									'placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary',
									error && 'border-destructive focus:ring-destructive/20 focus:border-destructive'
								)}
							/>
							{error && <p className="text-xs text-destructive">{error}</p>}
						</div>
						<div className="flex items-center justify-between">
							<span className="text-xs text-muted-foreground">
								{shiftHeld ? 'Will add more...' : 'Hold Shift to add multiple'}
							</span>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={() => onOpenChange(false)}
									className="px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
								>
									{addedCount > 0 ? 'Done' : 'Cancel'}
								</button>
								<button
									type="button"
									onClick={() => handleSubmit(shiftHeld)}
									disabled={!url.trim()}
									className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{shiftHeld ? 'Add + More' : 'Add'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Checking State */}
				{state === 'checking' && (
					<div className="flex flex-col items-center gap-3 py-6">
						<Loader2 className="size-8 animate-spin text-primary" />
						<p className="text-sm text-muted-foreground">Adding link...</p>
					</div>
				)}

				{/* Duplicate State - only shown when not in add-more mode */}
				{state === 'duplicate' && response && (
					<div className="space-y-4">
						<div className="flex flex-col items-center gap-3 py-2">
							<div className="size-12 rounded-full bg-amber-500/10 flex items-center justify-center">
								<ExternalLink className="size-6 text-amber-500" />
							</div>
							<div className="text-center">
								<p className="font-medium">Link Already Exists</p>
								{response.existingLink ? (
									<p className="text-sm text-muted-foreground mt-1">
										{response.existingLink.archivedAt ? (
											<span className="inline-flex items-center gap-1">
												<Archive className="size-3" />
												In Archive
											</span>
										) : response.existingLink.queueId === 0 ? (
											<span className="inline-flex items-center gap-1">
												<Inbox className="size-3" />
												In Inbox
											</span>
										) : (
											<span>In Queue #{response.existingLink.queueId}</span>
										)}
									</p>
								) : response.entry?.status ? (
									<p className="text-sm text-muted-foreground mt-1">
										Status: {response.entry.status}
									</p>
								) : null}
							</div>
						</div>

						<div className="flex justify-center gap-2">
							<button
								type="button"
								onClick={() => {
									setState('input');
									setUrl('');
									setError(null);
									setTimeout(() => inputRef.current?.focus(), 0);
								}}
								className="px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
							>
								Add Different
							</button>
							{response.existingLink && (
								<button
									type="button"
									onClick={handleGoToLink}
									className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
								>
									Go to Link
								</button>
							)}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
